import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { LoadingPanel } from '@/common/components/LoadingPanel/LoadingPanel'
import { useHistory } from '@/hooks'
import { useListMyCoursesQuery } from '@/services/api/tenant/learnerCoursesEnhanced'
import { RootState, useTypedSelector } from '@/store'

const MY_COURSES_PATH = '/my/courses'

export type LegacyStartCourseRedirectProps = {
  /** The course id carried by invitation mail sent before the rename. */
  courseId: string
}

/**
 * Forwards a pre-rename course link to the address that replaced it.
 *
 * The retired link names the course by id and the current one by code, so unlike the bare list
 * path this cannot be a static redirect: the code has to be looked up in the learner's own courses
 * first. Same query the start screen runs, so the lookup is shared rather than extra work.
 *
 * Forwarding rather than rendering in place is what makes the retirement self-healing - the
 * learner ends up on the canonical URL, so a bookmark taken from an old mail quietly becomes a
 * current one before the retired route is ever deleted.
 */
const LegacyStartCourseRedirect = ({ courseId }: LegacyStartCourseRedirectProps): JSX.Element => {
  const { t } = useTranslation('general')
  const history = useHistory()
  const tenant = useTypedSelector((state: RootState) => state.authSlice.tenant)
  const { data: courses, isLoading } = useListMyCoursesQuery({ tenantId: tenant.id })

  const courseCode = courses?.find((candidate) => candidate.course_id === courseId)?.course_code
  // An id matching nothing the learner is enrolled in has no course page to reach, so it lands on
  // the list - the same answer the start screen gives an unknown code.
  const courseMissing = !isLoading && courses !== undefined && courseCode === undefined

  useEffect(() => {
    if (courseCode !== undefined) history.replace(`${MY_COURSES_PATH}/${encodeURIComponent(courseCode)}`)
    else if (courseMissing) history.replace(MY_COURSES_PATH)
  }, [courseCode, courseMissing, history])

  return <LoadingPanel label={t('course_loading')} />
}

export default LegacyStartCourseRedirect
